/**
 * Single source of truth for service descriptors.
 * Consumed by: /api/inquiry (zod allowlist derives from SERVICE_IDS), the
 * engine (ServiceKind/GRID_SERVICES/POINT_TO_POINT_SERVICES re-point here),
 * passengers.ts (BookingType), and the M3 quote-boundary enforcement
 * (getServiceDescriptor + allowedAddOns).
 *
 * allowedAddOns is the all-for-all "valid/settable" column of the reconciled
 * add-on matrix (plan D-2): every service may SET every add-on — the kiosk
 * sends them all and the engine's own guards make inapplicable ones no-ops.
 * Pricing applicability lives in the engine (addons-schema `pricesFor` + the
 * variance.ts service guards), NEVER in this allowlist.
 */
import { ADDON_KEYS, type AddOnKey } from './addons-schema.js';

export type PricingMode = 'grid' | 'p2p' | 'manual';

export type ServiceDescriptor = {
  serviceId: string;
  label: string;
  /** How the engine prices this service. */
  pricingMode: PricingMode;
  /** Fields required beyond COMMON_REQUIRED ('service','passengers','date','name','phone'). */
  requiredFields: readonly string[];
  /**
   * @deprecated — unowned drifted data, do not consume; the next consumer earns
   * the right to define it.
   *
   * Q26 (2026-07-14, option c). It has ZERO readers and nothing has kept it
   * honest, so it drifted in both directions: 4 of its 5 declared names don't
   * exist on the form (`flightNumber` is really `flight`; `terminal`,
   * `returnTime`, `additionalDropoffs` don't exist), and it under-reports
   * (golf's `courseOther`, wedding's ceremony/reception times and airport's
   * `dropoffEircode` are all missing). Kept in place — NOT deleted — because it
   * is still served verbatim by /api/service-descriptors and is still a
   * non-optional field of 7 typed fixtures. Do not consume it, do not "fix" it
   * in the abstract: whoever next needs conditional fields defines the shape
   * against a real requirement.
   */
  conditionalFields: readonly string[];
  /** Add-on keys settable for this service (D-2: all-for-all — see module doc). */
  allowedAddOns: readonly AddOnKey[];
};

/**
 * NOTE: every `conditionalFields` entry below is @deprecated drifted data with
 * no owner and no reader — see the field's doc on ServiceDescriptor. The values
 * are deliberately left EXACTLY as they are: they ship in the
 * /api/service-descriptors payload, so editing them would be an unowned API
 * change. Don't consume them, don't tidy them.
 */
export const SERVICE_DESCRIPTORS = [
  {
    serviceId: 'airport',
    label: 'Airport Transfer',
    pricingMode: 'grid',
    // T384: direction-aware airport block — the airport end is a terminal
    // select (no free-text dropoff, no airport-end Eircode), the address end
    // is street+town+Eircode. `dropoff` is COMPOSED client-side from the
    // terminal (it must NOT be listed here: the required checker counts
    // disabled/absent inputs as missing).
    requiredFields: ['direction', 'terminal', 'pickup', 'town', 'pickupEircode'],
    conditionalFields: ['flightNumber', 'terminal'],
    allowedAddOns: ADDON_KEYS,
  },
  {
    serviceId: 'golf',
    label: 'Golf Transfer',
    pricingMode: 'grid',
    requiredFields: ['pickup', 'town', 'pickupEircode', 'course'],
    conditionalFields: [],
    allowedAddOns: ADDON_KEYS,
  },
  {
    serviceId: 'wedding',
    label: 'Wedding',
    pricingMode: 'manual',
    requiredFields: ['venue', 'customerEircode'],
    conditionalFields: ['venueOther', 'additionalDropoffs'],
    allowedAddOns: ADDON_KEYS,
  },
  {
    serviceId: 'private-tour',
    label: 'Private Tour',
    pricingMode: 'manual',
    requiredFields: ['pickup', 'town', 'pickupEircode', 'itinerary'],
    conditionalFields: ['returnTime'],
    allowedAddOns: ADDON_KEYS,
  },
  {
    serviceId: 'local-other',
    label: 'Custom / Other',
    pricingMode: 'p2p',
    requiredFields: ['pickup', 'town', 'pickupEircode', 'dropoff'],
    conditionalFields: ['returnTime'],
    allowedAddOns: ADDON_KEYS,
  },
  {
    // Nursing-home transport (POC, 2026-07-19). Operator-only (see
    // OPERATOR_ONLY_SERVICES) -- an account/B2B flow Helen sets up in the kiosk,
    // never on the public site. Manual pricing (Helen prices the run by hand,
    // like a wedding); a home->destination run reuses the pickup/dropoff capture.
    serviceId: 'nursing-home',
    label: 'Nursing Home Transport',
    pricingMode: 'manual',
    requiredFields: ['pickup', 'town', 'pickupEircode', 'dropoff'],
    conditionalFields: [],
    allowedAddOns: ADDON_KEYS,
  },
] as const satisfies readonly ServiceDescriptor[];

type ServiceDescriptors = typeof SERVICE_DESCRIPTORS;

/** Canonical service ids: 'airport' | 'golf' | 'wedding' | 'private-tour' | 'local-other'. */
export type ServiceId = ServiceDescriptors[number]['serviceId'];

/** Homomorphic tuple map — a GENERIC alias so TS preserves tuple-ness (an
 *  inline mapped type over the concrete tuple would enumerate array members). */
type ServiceIdsOf<T> = { [K in keyof T]: T[K] extends { serviceId: infer V } ? V : never };

/** Tuple of service ids in descriptor order (literal-typed, zod-enum ready). */
export const SERVICE_IDS = SERVICE_DESCRIPTORS.map(
  (s) => s.serviceId,
) as unknown as ServiceIdsOf<ServiceDescriptors>;

/**
 * T407 (Owen meeting 2026-07-17, D3): services no longer OFFERED for new work.
 * They stay in SERVICE_DESCRIPTORS so historical rows keep their label,
 * routing and rendering (getServiceDescriptor throws on unknown ids); only the
 * OFFERING surfaces — kiosk Home tiles, the public inquiry zod, the generated
 * site schema — filter on OFFERED_SERVICE_IDS. Module-scope export,
 * deliberately NOT a descriptor field (Q26: the per-descriptor wire shape must
 * stay byte-identical — 7 typed fixtures carry it).
 */
export const RETIRED_SERVICES = ['private-tour'] as const satisfies readonly ServiceId[];
export type RetiredServiceId = (typeof RETIRED_SERVICES)[number];

/**
 * Operator-only services (2026-07-19, nursing-home POC): account work Helen
 * books from the Nursing homes area, never surfaced on the public site.
 * Excluded from OFFERED_SERVICE_IDS, so the public inquiry zod (inquiry.ts) and
 * the generated site schema (gen-site-inquiry-schema) never carry them and their
 * e2e parity specs are untouched. ⭐ board 2814 M6 (DD-8): excluded from
 * KIOSK_SERVICE_IDS too, so the calculator has no tile for them; the
 * descriptor stays so labels and old bookings still resolve. Like
 * RETIRED_SERVICES this is a module-scope set, NOT a descriptor field (Q26
 * wire-shape precedent).
 */
export const OPERATOR_ONLY_SERVICES = ['nursing-home'] as const satisfies readonly ServiceId[];
export type OperatorOnlyServiceId = (typeof OPERATOR_ONLY_SERVICES)[number];

export type OfferedServiceId = Exclude<ServiceId, RetiredServiceId | OperatorOnlyServiceId>;

/** Service ids offered for NEW PUBLIC work, in descriptor order (zod-enum ready).
 *  Excludes both retired and operator-only services. */
export const OFFERED_SERVICE_IDS = SERVICE_IDS.filter(
  (id): id is OfferedServiceId =>
    !(RETIRED_SERVICES as readonly string[]).includes(id) &&
    !(OPERATOR_ONLY_SERVICES as readonly string[]).includes(id),
) as [OfferedServiceId, ...OfferedServiceId[]];

/**
 * Service ids an OPERATOR can start in the calculator, in descriptor order. The
 * kiosk Home tiles filter on THIS set (served as the descriptor endpoint's
 * `offeredServiceIds`); the public site uses OFFERED_SERVICE_IDS only.
 * ⭐ board 2814 M6 (DD-8): no nursing work in the calculator, so the
 * operator-only services are excluded as well as the retired ones. The two sets
 * hold the same ids today and stay two names, because they answer two
 * questions (what the public may ask for, what the calculator may start).
 */
export type KioskServiceId = Exclude<ServiceId, RetiredServiceId | OperatorOnlyServiceId>;
export const KIOSK_SERVICE_IDS = SERVICE_IDS.filter(
  (id): id is KioskServiceId =>
    !(RETIRED_SERVICES as readonly string[]).includes(id) &&
    !(OPERATOR_ONLY_SERVICES as readonly string[]).includes(id),
) as [KioskServiceId, ...KioskServiceId[]];

type DescriptorOfMode<M extends PricingMode> = Extract<
  ServiceDescriptors[number],
  { pricingMode: M }
>;

/** Services priced off the rate grid (airport radial + golf hubs). */
export type GridServiceId = DescriptorOfMode<'grid'>['serviceId'];
export const GRID_SERVICES: readonly GridServiceId[] = SERVICE_DESCRIPTORS.filter(
  (s): s is DescriptorOfMode<'grid'> => s.pricingMode === 'grid',
).map((s) => s.serviceId);

/**
 * Services priced point-to-point (A->B road distance), not radially to the
 * airport. They collect a pickup AND a destination and always estimate.
 */
export type PointToPointServiceId = DescriptorOfMode<'p2p'>['serviceId'];
export const POINT_TO_POINT_SERVICES: readonly PointToPointServiceId[] =
  SERVICE_DESCRIPTORS.filter((s): s is DescriptorOfMode<'p2p'> => s.pricingMode === 'p2p').map(
    (s) => s.serviceId,
  );

/** Hand-priced services (no computed base line; Helen types the total). */
export type ManualServiceId = DescriptorOfMode<'manual'>['serviceId'];
export const MANUAL_SERVICES: readonly ManualServiceId[] = SERVICE_DESCRIPTORS.filter(
  (s): s is DescriptorOfMode<'manual'> => s.pricingMode === 'manual',
).map((s) => s.serviceId);

export const COMMON_REQUIRED = ['service', 'passengers', 'date', 'name', 'phone'] as const;

/**
 * T384 (Coda F) — direction-aware airport block. New REGISTRY-OWNED enums
 * (consistency brief #8): the intake zod and the generated site schema both
 * derive from these. Deliberately NOT `conditionalFields` (@deprecated,
 * unowned — Q26): these are consumed, owned shapes with a real requirement.
 *
 * Values are slug-safe by design — the site-schema generator's quoteList
 * emitter refuses anything outside /^[A-Za-z0-9_+-]+$/. Customer wording
 * lives in site-owned copy maps (InquiryForm.astro), exhaustiveness-guarded.
 */
export const AIRPORT_DIRECTIONS = ['to-airport', 'from-airport'] as const;
export type AirportDirection = (typeof AIRPORT_DIRECTIONS)[number];

/**
 * Dublin Airport terminal options as the form offers them. 'not-sure' is
 * stored EXPLICITLY (never omitted for an answered airport inquiry), so key
 * absence keeps meaning "pre-T384 row / non-airport" — never "unsure".
 */
export const AIRPORT_TERMINALS = ['t1', 't2', 'not-sure'] as const;
export type AirportTerminal = (typeof AIRPORT_TERMINALS)[number];

/**
 * T608 M6b (R5) — the terminal token as it is RECORDED on the booking.
 *
 * Not customer copy (that stays site-owned, above): this is the designation
 * that lands in `booking_stops.address_line` on the terminal row and is read
 * back by the operator screens and the driver line. It lives here, beside the
 * vocabulary, because TWO writers in two layers need the same answer —
 * `buildStops` (core/normalise.ts, the intake path, off `inquiries.extra`) and
 * `inquiryAddressLines` (client/lib/actions.ts, the quote prefill that feeds
 * `journeyStops`). Two maps would let intake and re-quote record different
 * strings for one journey. Neither writer reads this map directly: both go
 * through `terminalAddressLine` below, which is also where the two conjuncts
 * that make a write SAFE are enforced.
 *
 * 'T1'/'T2' is Dublin Airport's own signage, and deliberately SHORTER than the
 * composed place label ('Dublin Airport Terminal 1') that sits in `address`:
 * BookingDetail's driver line prints `address: address_line`, so a designation
 * equal to the label would read the terminal twice on one line. That is the
 * DISPLAY reason; the safety of the two columns differing is not left to it
 * (see `terminalAddressLine`).
 *
 * 'not-sure' maps to null: "not sure" is not a terminal, and recording it as
 * one would put a claim on the record the customer never made. The asked-but-
 * unknown distinction survives where it already lives, on `inquiries.extra`.
 * simplified: the asked-but-unknown case is not surfaced to the driver, add
 * when Owen asks for it.
 */
const AIRPORT_TERMINAL_DESIGNATION: Record<AirportTerminal, string | null> = {
  't1': 'T1',
  't2': 'T2',
  'not-sure': null,
};

/** The recorded designation for a stored terminal token, or null when there is
 *  none to record (unknown/absent token, or an explicit 'not-sure'). Accepts
 *  `string | null` because the token is read off untyped JSONB. Called by
 *  `terminalAddressLine` below, which is what the two writers use.
 *
 *  `Object.hasOwn`, not a bare index — the same guard, for the same reason, as
 *  `loneStopRole` in journey-ends.ts and `bookingTypeLabel` in status-schema.ts.
 *  AIRPORT_TERMINAL_DESIGNATION is a plain object literal, so EVERY own property
 *  of `Object.prototype` reaches a TRUTHY non-string through the prototype
 *  chain — '__proto__' returns Object.prototype, and 'constructor' / 'toString'
 *  (and the rest) return native functions. A `?? null` tail can never fire for
 *  any of them, and downstream that value reaches `.trim()` in `journeyStops`
 *  (TypeError — the save crashes) or becomes a pg parameter. Not reachable
 *  through today's writers (`server/inquiry.ts` validates with
 *  `z.enum(AIRPORT_TERMINALS)`), but the token is read back off untyped JSONB,
 *  which is exactly where the guard earns its call. */
export function terminalDesignation(token: string | null | undefined): string | null {
  if (token == null || !Object.hasOwn(AIRPORT_TERMINAL_DESIGNATION, token)) return null;
  return AIRPORT_TERMINAL_DESIGNATION[token as AirportTerminal];
}

/**
 * T608 M6b (ux close-out) — is a captured value the terminal designation ITSELF?
 *
 * The inverse direction to `terminalDesignation`: that maps a stored token to
 * the string to record; this recognises that string coming back the other way,
 * out of an editable field, so the UI can say what it is. It lives HERE, beside
 * the map, for the same reason the map does — a hardcoded 'T1'/'T2' at the
 * component would be a second definition of "terminal designation", free to
 * drift from the one the writers record from.
 *
 * Trimmed and case-insensitive on both sides, because this reads an EDITABLE
 * input's value rather than a column this module wrote: ' t1' is the same code,
 * carries the same do-not-edit cost, and needs the same caption. Derived from
 * the map's own values (never a second literal list), so a new terminal is
 * recognised the moment it is recorded. 'not-sure' maps to null and so matches
 * nothing — there is no code to explain.
 */
export function isTerminalDesignation(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v === '') return false;
  return Object.values(AIRPORT_TERMINAL_DESIGNATION)
    .some((d) => d !== null && d.toLowerCase() === v);
}

/**
 * T608 M6b (R5, remediation) — the designation to RECORD on ONE stop row, and
 * the place where the two conjuncts that make it SAFE are enforced rather than
 * hoped for. Both writers call this, never `terminalDesignation` directly:
 * `buildStops` (core/normalise.ts, intake) and `inquiryAddressLines`
 * (client/lib/actions.ts, the quote prefill that feeds `journeyStops`).
 *
 * 1. DESIGNATION ≠ THE ROW'S OWN PLACE LABEL. `pickupLabelIsStreet`
 *    (client/lib/actions.ts, T540) reads a pickup row whose
 *    `address === address_line` as "the label IS the street the operator typed"
 *    and deliberately DROPS the label, so an arrivals reopen of such a row
 *    discards the airport end and re-persists the customer's town as the pickup
 *    (measured: ['Kilcoole', '5 Seafront Cottages, Kilcoole'] — the airport gone
 *    from the journey, and that pickup reaches the customer in the quote SMS).
 *    `address` comes off the PUBLIC inquiry form, independently of the token, so
 *    a submission carrying `pickup: 'T1'` + `terminal: 't1'` produces exactly
 *    that row. The short form being shorter than the composed label is a
 *    property of today's vocabulary; this compare is the guarantee. Exactly
 *    `===`, the guard's own compare, so this refuses precisely the rows the
 *    guard would trap and no others — and nothing is lost when it fires, because
 *    a designation equal to `address` says nothing `address` does not.
 * 2. A RECOGNISED DIRECTION. `extra.direction` is `.optional()` in the intake
 *    zod and `requiredFields` is not enforced server-side, so an airport
 *    submission can omit it. Direction is what decides WHICH END is the
 *    terminal; both writers default to the departures arm without one, so they
 *    stamped the designation onto the customer's OWN address row
 *    ('5 Seafront Cottages, Kilcoole: T2' on the driver line — a claim the
 *    customer never made). No direction => no designation, on either end.
 *
 * `place` is the `address` the SAME row will carry, which each caller knows and
 * this function cannot: intake writes `inq.pickup`/`inq.dropoff`, and the quote
 * prefill passes those same two values, which are what `pickupEndPlace` /
 * `dropoffEndPlace` then carry into `journeyStops`.
 */
export function terminalAddressLine(args: {
  /** `extra.terminal` verbatim (service-gated by the caller). */
  token: string | null | undefined;
  /** `extra.direction` verbatim — anything outside AIRPORT_DIRECTIONS records nothing. */
  direction: string | null | undefined;
  /** The place label this row already carries. */
  place: string | null;
}): string | null {
  if (!(AIRPORT_DIRECTIONS as readonly string[]).includes(args.direction ?? '')) return null;
  const designation = terminalDesignation(args.token);
  return designation === null || designation === args.place ? null : designation;
}

/**
 * T387 (rates-book) — price-entry kinds. What KIND of price-book entry each
 * service's standing prices are: airport prices towns, golf prices routes,
 * wedding/private-tour price venues. local-other is deliberately ABSENT —
 * point-to-point has no single-location price key (raised, not forced; see the
 * plan's ordered-lookup scope decision).
 *
 * Module-scope exports, deliberately NOT fields on SERVICE_DESCRIPTORS: the
 * /api/service-descriptors wire payload must stay byte-identical (the Q26
 * fixture-sensitivity precedent — 7 typed fixtures carry the descriptor shape).
 */
/**
 * WHAT THE CUSTOMER IS TOLD THE SERVICE IS, where the internal label is not fit
 * to send (T1679, board #1679).
 *
 * `SERVICE_LABELS` is the OPERATOR vocabulary - Helen's screens, the dispatcher,
 * the driver and deposit-paid alerts. `local-other` is labelled "Custom / Other"
 * there, which is the correct name for a catch-all bucket on a kiosk screen and
 * is not something to send a member of the public: a real quote email read "Your
 * quote for Custom / Other is EUR360". `serviceToBookingType` also folds
 * `nursing-home` into `local-other`, so nursing jobs read the same, and the
 * wording below has to work for both.
 *
 * ⛔ A MODULE-SCOPE MAP, DELIBERATELY NOT A DESCRIPTOR FIELD - the Q26 wire-shape
 * precedent this file already applies twice above (`RETIRED_SERVICES`,
 * `OPERATOR_ONLY_SERVICES`) and once below (`PRICE_ENTRY_KIND_BY_SERVICE`).
 * `server/app.ts` returns `services: SERVICE_DESCRIPTORS` with NO projection, so
 * a new descriptor field ships verbatim on `/api/service-descriptors` and into
 * the browser bundle. Unlike the add-on registry - which has an explicit
 * `clientAddOnDescriptors()` projection AND a spec pinning its exact key set -
 * nothing here would go red, so this is a review-only guarantee and the reason
 * the shape is chosen rather than the shape being incidental.
 *
 * ⚠️ PARTIAL ON PURPOSE. Absent means "the operator label is already fit to
 * send", which is true of all five other services; only the catch-all needs a
 * translation. Do not fill this in for the sake of completeness - a second
 * spelling of "Airport Transfer" is a drift risk with no reader.
 */
export const CUSTOMER_SERVICE_LABELS: Readonly<Partial<Record<ServiceId, string>>> = {
  // Owen/Alex may reword this freely - it is one constant and nothing derives
  // from its bytes. It must stay true for a nursing-home job, which folds here.
  'local-other': 'Minibus Hire',
};

export const PRICE_ENTRY_KINDS = ['town', 'route', 'venue'] as const;
export type PriceEntryKind = (typeof PRICE_ENTRY_KINDS)[number];

export const PRICE_ENTRY_KIND_BY_SERVICE: Readonly<
  Partial<Record<ServiceId, PriceEntryKind>>
> = {
  airport: 'town',
  golf: 'route',
  wedding: 'venue',
  'private-tour': 'venue',
  // 'local-other': none — p2p, no single-location key
};

/** Look up a descriptor by serviceId; throws if not found. */
export function getServiceDescriptor(serviceId: string): ServiceDescriptor {
  const d = SERVICE_DESCRIPTORS.find((s) => s.serviceId === serviceId);
  if (!d) throw new Error(`Unknown serviceId: ${serviceId}`);
  return d;
}

/** Map of serviceId → label (mirrors the old kiosk SERVICE_LABELS). */
export const SERVICE_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  SERVICE_DESCRIPTORS.map((s) => [s.serviceId, s.label]),
);
